import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Loader2, FileCheck2, Download, ScanFace, MessageSquare, Mail } from "lucide-react";

const methodBadge = (chan) => {
  const c = (chan || "").toLowerCase();
  if (c === "facial")
    return { label: "Facial", icon: ScanFace, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" };
  if (c === "email")
    return { label: "E-mail", icon: Mail, cls: "bg-sky-50 text-sky-700 border-sky-200" };
  if (c === "whatsapp" || c === "wpp")
    return { label: "WhatsApp", icon: MessageSquare, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  return { label: "SMS", icon: MessageSquare, cls: "bg-slate-50 text-slate-700 border-slate-200" };
};

export const SignatureReceiptsDialog = ({ open, onOpenChange, list }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const load = useCallback(async () => {
    if (!list?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/courses/lists/${list.id}/signatures`, authHeaders);
      setItems((r.data?.items || []).filter((i) => i.status === "signed"));
    } catch (e) {
      toast.error("Erro ao carregar assinaturas");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const download = async (sig) => {
    setDownloadingId(sig.id);
    try {
      const r = await axios.get(
        `${API}/admin/signatures/${sig.id}/receipt`,
        { ...authHeaders, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `comprovante_${sig.matricula}_${sig.receipt_code}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao baixar o comprovante");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="receipts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-emerald-600" />
            Comprovantes de assinatura
          </DialogTitle>
          <DialogDescription>
            {list?.training_date ? `Lista de ${list.training_date}. ` : ""}
            Comprovante individual com método, IP, dispositivo, hash e QR de
            validação — disponível para assinaturas feitas a partir de agora.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            Nenhuma assinatura concluída nesta lista ainda.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((sig) => {
              const mb = methodBadge(sig.otp_channel);
              const MbIcon = mb.icon;
              return (
                <div key={sig.id} className="py-2.5 flex items-center gap-3" data-testid={`receipt-row-${sig.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {sig.nome}
                      <span className="text-slate-400 font-normal"> ({sig.matricula})</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {sig.signed_at ? new Date(sig.signed_at).toLocaleString("pt-BR") : "-"}
                      {sig.receipt_code ? ` • ${sig.receipt_code}` : ""}
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${mb.cls}`}>
                    <MbIcon className="w-3 h-3 mr-1" /> {mb.label}
                  </Badge>
                  {sig.receipt_code ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => download(sig)}
                      disabled={downloadingId === sig.id}
                      data-testid={`receipt-download-${sig.id}`}
                    >
                      {downloadingId === sig.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <><Download className="w-3.5 h-3.5 mr-1" /> Comprovante</>}
                    </Button>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic w-[104px] text-center">
                      assinatura anterior ao recurso
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
